<template>
  <div class="p-4 sm:p-6">
    <FileUpload @files-uploaded="handleFilesUploaded" class="mb-8" :disabled="uploading" />

    <div v-if="loading" class="flex justify-center items-center py-12">
      <UIcon name="i-heroicons-arrow-path" class="w-8 h-8 animate-spin text-gray-400" />
    </div>

    <div v-else-if="allDocuments.length > 0">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        <div v-for="doc in allDocuments" :key="doc.key" class="group relative bg-(--ui-bg) rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all duration-200 p-3">
          <div class="flex items-start gap-3">
            <div class="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center" :class="getFileTypeInfo(doc.contentType).bg">
              <UIcon :name="getFileTypeInfo(doc.contentType).icon" class="text-xl" :class="getFileTypeInfo(doc.contentType).color" />
            </div>

            <div class="flex-1 min-w-0 pt-0.5">
              <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate mb-1" :title="doc.name">
                {{ doc.name }}
              </h4>
              <div class="flex flex-col gap-0.5 text-xs text-gray-500 dark:text-gray-400">
                <span class="flex items-center gap-1 text-gray-500 dark:text-gray-400 truncate" :title="formatDate(doc.date, 'datetime', locale)">
                  <UIcon name="i-heroicons-calendar" class="w-3 h-3" />
                  {{ formatDate(doc.date, 'datetime', locale) }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700/50">
            <div v-if="doc.source === 'local'" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-(--ui-bg-muted) text-xs font-medium text-gray-600 dark:text-gray-300">
              {{ formatFileSize(doc.size ?? 0) }}
            </div>
            <div v-else class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-primary-50 dark:bg-primary-900/20 text-xs font-medium text-primary-600 dark:text-primary-400 min-w-0" :title="doc.documentType">
              <UIcon name="i-heroicons-cloud" class="w-3 h-3 flex-shrink-0" />
              <span class="truncate">{{ doc.documentType }}</span>
            </div>

            <div class="flex items-center justify-end gap-1">
              <UTooltip v-if="canView(doc)" text="View">
                <UButton
                  variant="ghost"
                  color="gray"
                  size="xs"
                  icon="i-heroicons-eye"
                  class="hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 transition-colors"
                  @click="viewDocument(doc)"
                />
              </UTooltip>
              <UTooltip text="Download">
                <UButton
                  variant="ghost"
                  color="gray"
                  size="xs"
                  icon="i-heroicons-arrow-down-tray"
                  class="hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 transition-colors"
                  @click="downloadDocument(doc)"
                  :loading="downloadingId === doc.key"
                />
              </UTooltip>
              <!--
              <div class="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1"></div>
              <UTooltip text="Delete">
                <UButton
                  variant="ghost"
                  color="gray"
                  size="xs"
                  icon="i-heroicons-trash"
                  class="hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-colors"
                  @click="deleteDocument(doc)"
                />
              </UTooltip>
              -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-12">
      <UIcon name="i-heroicons-document-magnifying-glass" class="text-5xl text-gray-400 dark:text-gray-500 mx-auto mb-4" />
      <h3 class="text-lg font-semibold text-gray-800 dark:text-gray-200">{{ t('documents.noDocumentsFound') }}</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ t('documents.uploadHint') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { formatDate } from "@/utils/date.ts";
import { useI18n } from "vue-i18n";
import { api, type LocalFileDto, type ItemDto, type WorkflowDto, type FileResponse, type ApiResult } from '@/api/eOrdersApi';
import { useApiAdapter } from "@/store";
import { showSuccess } from "@/utils/utils.ts";
import { formatFileSize, getFileTypeInfo, guessContentType } from "@/utils/fileUtils";
import { filterDocuments } from "@/utils/documentUtils";
import FileUpload from '@/components/FileUpload.vue';

type DisplayDocument = {
  key: string;
  source: 'local' | 'contentManager';
  name: string;
  contentType: string;
  date: string;
  size?: number;
  documentType?: string;
  local?: LocalFileDto;
  cm?: ItemDto;
};

const props = defineProps<{
  workflow: WorkflowDto | null;
}>();

const { t, locale } = useI18n();
const apiAdapter = useApiAdapter();

const documents = ref<LocalFileDto[]>([]);
const contentManagerItems = ref<ItemDto[]>([]);
const loading = ref(false);
const uploading = ref(false);
const downloadingId = ref<string | null>(null);

const emit = defineEmits(['documents-changed'])

const allDocuments = computed<DisplayDocument[]>(() => {
  const local: DisplayDocument[] = documents.value.map(doc => ({
    key: `local-${doc.id}`,
    source: 'local',
    name: doc.originalName,
    contentType: doc.contentType,
    date: doc.uploadedAt,
    size: doc.size,
    local: doc,
  }));
  
  const cm: DisplayDocument[] = contentManagerItems.value.map(item => ({
    key: `cm-${item.pid}`,
    source: 'contentManager',
    name: item.fileName ?? item.pid,
    contentType: guessContentType(item.fileName),
    date: item.createdTimestamp,
    documentType: humanizeDocumentType(item.documentType ?? item.itemtypeName),
    cm: item,
  }));
  
  return [...local, ...cm].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
});

const loadDocuments = async () => {
  const externalReference = props.workflow?.externalReference;
  if (!externalReference) return;

  loading.value = true;

  try {
    const [localResult, cmResult] = await Promise.all([
      apiAdapter.invoke<LocalFileDto[]>(
        requestConfig => api.localFilesGetfiles(externalReference, requestConfig),
        (err) => {
          console.error('Error loading documents:', err);
        }
      ),
      apiAdapter.invoke<ItemDto[]>(
        requestConfig => api.getWorkflowContentManager(externalReference, requestConfig),
        (err) => {
          console.error('Error loading content manager items:', err);
        }
      ),
    ]);
    
    documents.value = localResult ?? [];
    
    const rawCm: any = cmResult;
    const cmList: ItemDto[] = Array.isArray(rawCm) ? rawCm : (rawCm?.$values ?? []);
    contentManagerItems.value = filterDocuments(cmList, 'vehicleDeliveryForm');
    
    emit('documents-changed', allDocuments.value.length);
  } catch (err) {
    console.error('Failed to load documents:', err);
  } finally {
    loading.value = false;
  }
}

const handleFilesUploaded = async (files: File[]) => {
  if (!files.length) return;

  const externalReference = props.workflow?.externalReference;
  if (!externalReference) return;

  uploading.value = true;

  try {
    const uploadPromises = files.map(file => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', file.name);

      return apiAdapter.invoke<LocalFileDto>(
        (config) => api.localFilesUpload(externalReference, file.name, { ...config, data: formData as unknown as LocalFileDto }),
        'documents.uploadError',
        false
      );
    });

    await Promise.all(uploadPromises);
    showSuccess(t('documents.uploadSuccess'));
    await loadDocuments();
  } finally {
    uploading.value = false;
  }
}

const fetchDocumentBlob = async (doc: DisplayDocument): Promise<{ blob: Blob; fileName: string } | null> => {
  const externalReference = props.workflow?.externalReference;
  if (!externalReference) return null;
  
  if (doc.source === 'local' && doc.local) {
    const result = await apiAdapter.invokeRaw<FileResponse>(
      (config) => api.localFilesDownload(externalReference, doc.local!.id, config),
      'documents.downloadError'
    );
    
    if (result?.type === 'success' && result.data) {
      return { blob: result.data.data, fileName: result.data.fileName || doc.name };
    }
    return null;
  }
  
  if (doc.source === 'contentManager' && doc.cm) {
    const result = await apiAdapter.invokeRaw<Blob>(
      (config) => api.downloadWorkflowContentManager(externalReference, { pid: doc.cm!.pid }, { ...config, responseType: 'blob' }) as unknown as Promise<ApiResult<Blob, void>>,
      'documents.downloadError'
    );
    
    if (result?.type === 'success' && result.response?.data) {
      return { blob: result.response.data as Blob, fileName: doc.name };
    }
    return null;
  }
  
  return null;
}

const downloadDocument = async (doc: DisplayDocument) => {
  downloadingId.value = doc.key;
  let url: string | null = null;

  try {
    const file = await fetchDocumentBlob(doc);
    if (!file) return;
    
    url = window.URL.createObjectURL(file.blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    
    document.body.removeChild(link);
  } finally {
    if (url) window.URL.revokeObjectURL(url);
    downloadingId.value = null;
  }
}

const canView = (doc: DisplayDocument): boolean => {
  const extension = doc.name.split('.').pop()?.toLowerCase();
  return extension !== 'zip' && !doc.contentType?.includes('zip');
}

const viewDocument = async (doc: DisplayDocument) => {
  const file = await fetchDocumentBlob(doc);
  if (!file) return;
  
  const url = window.URL.createObjectURL(file.blob);
  window.open(url, '_blank');
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

const humanizeDocumentType = (value: string | undefined | null): string => {
  if (!value) return '';
  
  const spaced = value.replace(/_/g, ' ').trim();
  
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

onMounted(() => {
  loadDocuments();
})

watch(() => props.workflow?.externalReference, () => {
  loadDocuments();
})
</script>